/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  CreateGroupModalPayload,
  EditGroupModalPayload
} from '@space-lib/components/groupModal/types'
import { requester, apiSparkle, apiCommunity, requesterPublic } from '@space-shell/config/request'

import type { ChangeCommunityTypeProps } from '@space-shell/modules/communities/hooks/useChangeCommunityTypeMutation/types'

import {
  CommunitiesEndpoints,
  AMOUNT_ROWS_GET_BLOCKED_FOLLOWERS,
  TOTAL_ROWS_GET_COURSES
} from './constants'
import type {
  CommunityProps,
  CreateCommunityProps,
  EditCommunityProps,
  CommunityGroupCreateResponse,
  CommunityGroupGetResponse,
  GetBlockedFollowersParams,
  ResponseGetBlockedFollowers,
  PutUnblockUserParams,
  CommunitiesCourseListResponse,
  GetFollowersRolesRequestFilters,
  GetFollowersRolesRequestResponse,
  EditFollowerRoleArgs,
  CreateCommunityPermission,
  CreateCommunityPermissionArgs,
  DeleteFollowerArgs,
  ExpirationDateArgs
} from './types'

class CommunitiesService {
  static getCommunity = async (id?: number): Promise<CommunityProps> => {
    if (!id) {
      return undefined as unknown as CommunityProps
    }

    const { data } = await requester.get<CommunityProps>(CommunitiesEndpoints.getCommunity(id))

    return data
  }

  static deleteCommunity = async (id: number): Promise<void> => {
    await requester.delete(CommunitiesEndpoints.deleteCommunity(id))
  }

  static editCommunity = async (payload: EditCommunityProps): Promise<CommunityProps> => {
    const { data } = await requester.put<CommunityProps>(
      CommunitiesEndpoints.editCommunity(Number(payload?.id)),
      payload
    )

    return data
  }

  static createCommunity = async (slug: string, payload: CreateCommunityProps) => {
    const { data } = await requester.post<CommunityProps>(
      `${CommunitiesEndpoints.createCommunity(slug)}/`,
      payload
    )

    return data
  }

  static getGroupCommunities = async (
    slug: string,
    rows: number
  ): Promise<CommunityGroupGetResponse> => {
    const { data } = await requester.get<CommunityGroupGetResponse>(
      `${CommunitiesEndpoints.getGroupCommunities(slug)}?rows=${rows}`
    )

    return data
  }

  static getGroupCommunitiesPublic = async (
    slug: string,
    rows: number
  ): Promise<CommunityGroupGetResponse> => {
    const { data } = await requesterPublic.get<CommunityGroupGetResponse>(
      `${CommunitiesEndpoints.getGroupCommunitiesPublic(slug)}?rows=${rows}`
    )

    return data
  }

  static createGroupCommunity = async (payload: CreateGroupModalPayload) => {
    const { data } = await requester.post<CommunityGroupCreateResponse>(
      `${CommunitiesEndpoints.createGroupCommunity()}`,
      payload
    )

    return data
  }

  static changeCommunityType = async (payload: ChangeCommunityTypeProps) => {
    const removeUsersQuery = `?removeUsers=${payload.removeUsers}`

    const { data } = await apiCommunity.put<void>(
      `${CommunitiesEndpoints.changeCommunityType(
        payload.communityId,
        payload.type
      )}${removeUsersQuery}`
    )

    return data
  }

  static editGroupCommunity = async (payload: EditGroupModalPayload) => {
    const { data } = await requester.put<void>(
      `${CommunitiesEndpoints.editGroupCommunity(payload.id)}`,
      payload
    )

    return data
  }

  static deleteGroupCommunity = async (groupId: number, slug: string) => {
    const { data } = await requester.delete<void>(
      CommunitiesEndpoints.deleteGroupCommunity(groupId, slug)
    )

    return data
  }

  static setLastViewDate = async (communityId: number) => {
    const { data } = await apiSparkle.put<unknown>(CommunitiesEndpoints.lastViewDate(communityId), {
      id: communityId
    })

    return data
  }

  static getBlockedFollowers = async ({
    communityId,
    query,
    pageParam = 0,
    rows = AMOUNT_ROWS_GET_BLOCKED_FOLLOWERS
  }: GetBlockedFollowersParams) => {
    const hasQuery = query ? `&query=${query}` : ''

    const { data } = await requester.get<ResponseGetBlockedFollowers>(
      `${CommunitiesEndpoints.getBlockedFollowers(
        communityId
      )}?&page=${pageParam}&rows=${rows}${hasQuery}`
    )

    return data
  }

  static putUnBlockUser = async ({ communityId, idUser }: PutUnblockUserParams) => {
    const { data } = await requester.put<void>(
      `${CommunitiesEndpoints.putUnBlockUser(communityId, idUser)}`
    )

    return data
  }

  static getCourses = async (
    slug: string,
    idCommunity: string,
    nextPageToken?: string
  ): Promise<CommunitiesCourseListResponse> => {
    const nextPage = nextPageToken ? `&page=${nextPageToken}` : ''
    const { data } = await requester.get<CommunitiesCourseListResponse>(
      `${CommunitiesEndpoints.getCourses(
        slug
      )}?idCommunity=${idCommunity}&size=${TOTAL_ROWS_GET_COURSES}${nextPage}`
    )

    return data
  }

  static associateCommunity = async (slug: string, body: any): Promise<void> => {
    await apiSparkle.put<Record<string, any>>(CommunitiesEndpoints.updateCourses(slug), body)
  }

  static getFollowersRoles = async (
    communityId: number,
    reqParams: GetFollowersRolesRequestFilters = {} as GetFollowersRolesRequestFilters
  ): Promise<GetFollowersRolesRequestResponse> => {
    const p = {
      rows: AMOUNT_ROWS_GET_BLOCKED_FOLLOWERS,
      ...reqParams
    }
    const params = Object.fromEntries(Object.entries(p).filter(([_, value]) => value !== undefined))

    const { data } = await apiCommunity.get<GetFollowersRolesRequestResponse>(
      CommunitiesEndpoints.getFollowersRoles(communityId),
      {
        params
      }
    )

    return data
  }

  static editFollowerRole = async ({
    communityId,
    userId,
    body
  }: EditFollowerRoleArgs): Promise<void> => {
    await apiSparkle.put<void>(CommunitiesEndpoints.editFollowerRole(communityId, userId), body)
  }

  static getCreateCommunityPermission = async ({
    featureName
  }: CreateCommunityPermissionArgs): Promise<CreateCommunityPermission> => {
    const url = CommunitiesEndpoints.getCreateCommunityPermission(featureName)
    const { data } = await apiSparkle.get<CreateCommunityPermission>(url)

    return data
  }

  static deleteFollower = async ({ communityId, ucode }: DeleteFollowerArgs): Promise<void> => {
    await apiCommunity.delete<void>(CommunitiesEndpoints.deleteFollower(communityId, ucode))
  }

  static expirationDate = async ({
    ucode,
    hashImportedId,
    expirationDate
  }: ExpirationDateArgs): Promise<void> => {
    await apiCommunity.put<void>(
      `${CommunitiesEndpoints.expirationDate(
        ucode,
        hashImportedId
      )}?expirationDate=${expirationDate}`
    )
  }
}

export default CommunitiesService
